import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';

const parseDate = (_: Dayjs, originalValue: Dayjs | null): Date | null =>
  dayjs(originalValue).isValid() ? dayjs(originalValue).toDate() : null;

// ========== SHARED HELPERS ==========

const createIdArraySchema = (fieldName: string, maxItems = 500): yup.Schema =>
  yup
    .array()
    .defined()
    .max(
      maxItems,
      `${fieldName} field must have less than or equal to ${maxItems} items`,
    );

const createStringArraySchema = (
  fieldName: string,
  maxItems = 500,
): yup.Schema =>
  yup
    .array()
    .of(yup.string().required())
    .defined()
    .max(
      maxItems,
      `${fieldName} field must have less than or equal to ${maxItems} items`,
    );

const createEntityArraySchema = (maxItems = 500): yup.Schema =>
  yup.array().defined().max(maxItems);

const createDateRangeSchema = (): yup.Schema =>
  yup
    .array()
    .of(yup.date().transform(parseDate).nullable().defined())
    .min(2)
    .max(2)
    .defined();

const createAutocompleteSelectOptionSchema = (): yup.Schema =>
  yup
    .object()
    .shape({
      value: yup.string().defined(),
      label: yup.string().defined(),
    })
    .defined();

const createVoidExclusiveStatusArraySchema = (): yup.Schema =>
  yup
    .array()
    .of(createAutocompleteSelectOptionSchema())
    .defined()
    .test(
      'void-exclusive',
      'When selecting void, no other options can be selected',
      (values): boolean => {
        if (values?.some(({ value }): boolean => value === 'VOID')) {
          return values.length === 1;
        }
        return true;
      },
    );

// ========== SHARED FIELDS ==========

const commonFilterFields = {
  source: yup.string().nullable().defined(),
  sourceIds: createIdArraySchema('Source ID'),
  sourceDateRange: createDateRangeSchema(),
  clients: createEntityArraySchema(),
  insurers: createEntityArraySchema(),
  partnerships: createEntityArraySchema(),
  billIds: createIdArraySchema('Bill ID'),
  userIds: createIdArraySchema('User ID'),
  billingTypes: yup
    .array()
    .of(createAutocompleteSelectOptionSchema())
    .defined(),
};

// ========== BILL FILTERS ==========

export const AdvancedBillManagementFiltersValidationSchema = yup.object({
  ...commonFilterFields,
  billStatus: yup
    .object()
    .shape({
      held: yup.boolean().required('Held is required').defined(),
      submitted: yup.boolean().required('Submitted is required').defined(),
    })
    .required('Bill status is required')
    .default({ held: false, submitted: false })
    .defined(),
  holdReasons: yup.array().of(createAutocompleteSelectOptionSchema()).defined(),
  dateRange: createDateRangeSchema(),
  submissionStatusTypes: createVoidExclusiveStatusArraySchema(),
  paymentTypes: yup
    .array()
    .of(createAutocompleteSelectOptionSchema())
    .defined(),
});

// ========== SUBMISSION FILTERS ==========

export const SubmissionAdvancedFiltersValidationSchema = yup.object({
  ...commonFilterFields,
  submissionIds: createIdArraySchema('Submission ID'),
  transactionIds: createStringArraySchema('Transaction ID'),
  submissionStatusTypes: createVoidExclusiveStatusArraySchema(),
  submissionDateRange: createDateRangeSchema(),
  netsuiteIds: createStringArraySchema('NetSuite ID'),
  creditMemoIds: createStringArraySchema('Credit Memo ID'),
});
